import HBLoading from "@/components/HBLoading";
import { useLoadingLimit } from "@/components/LoginModal/useLoginLimit";
import PinList from "@/components/PinList";
import useAppStore from "@/context/store";
import { getTopicPins } from "@/services";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { useMemoizedFn } from "ahooks";
import { Button } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";

function TopicEmpty({ title }) {
  return (
    <div
      data-gd-expose="resource_loc_expose"
      data-resource-title="没有更多了"
      data-resource-type="文字"
      className={styles.noMore}
    >
      {title ? `更多关于「${title}」内容即将更新，敬请期待～` : "没有更多了"}
    </div>
  );
}

export function TopicLimitPinList({
  values,
  defaultPins,
  showDownload,
  onEmptyWithDefaultSort,
  isRemix,
  title,
}) {
  const [dataSource, setDataSource] = useState(defaultPins || []);
  const [loading, setLoading] = useState(defaultPins?.length <= 0);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);
  const initRef = useRef(defaultPins?.length > 0);
  const userReady = useAppStore((s) => s.userReady);
  const hasTriedTimeSortRef = useRef(false);

  // 当排序改变时，重置尝试时间排序的标志
  useEffect(() => {
    if (values?.sort !== "all" && values?.sort) {
      hasTriedTimeSortRef.current = false;
    }
  }, [values?.sort]);

  const queryAction = useMemoizedFn(async () => {
    setLoading(true);
    const result = await getTopicPins({
      per_page: isRemix ? 40 : 80,
      page: pageRef.current,
      filter_ids: values?.ids || [],
      sort: isRemix ? "time" : values?.sort || "all",
      position: "topics",
    });

    const pins = result?.pins || [];

    // 如果使用综合排序（"all"）且返回空结果，且还没有尝试过时间排序，则通知父组件切换排序
    if (
      pins.length <= 0 &&
      pageRef.current === 1 &&
      (values?.sort === "all" || !values?.sort) &&
      !hasTriedTimeSortRef.current &&
      onEmptyWithDefaultSort
    ) {
      hasTriedTimeSortRef.current = true;
      onEmptyWithDefaultSort();
      setLoading(false);
      return;
    }

    if (pins.length <= 0) {
      setHasMore(false);
    }

    setDataSource((dataSource) => dataSource.concat(pins));
    setLoading(false);
  });

  // 监听values变化，重新请求数据
  useEffect(() => {
    // 有初始数据时，不进行第一次请求
    if (initRef.current) {
      initRef.current = false;
      return;
    }

    // 重置数据
    pageRef.current = 1;
    setDataSource([]);
    setLoading(true);
    setHasMore(true);

    queryAction();
  }, [values, onEmptyWithDefaultSort, queryAction]);

  const { onLoadLimitCheck } = useLoadingLimit({
    pageListData: dataSource,
  });

  const [loadingLimit, setLoadingLimit] = useState(false);
  const onLoadMore = useMemoizedFn(async () => {
    if (dataSource.length > 0) {
      const res = await onLoadLimitCheck();

      if (res) {
        pageRef.current = pageRef.current + 1;
        queryAction();
      } else {
        setLoadingLimit(true);
      }
    }
  });

  const onLoginLimit = useMemoizedFn(() => {
    setLoadingLimit(true);
  });

  return (
    <div
      className={styles.list}
      data-sort-type={values?.sortName}
      data-filter-one={values?.filterOne}
    >
      <If condition={dataSource.length > 0}>
        <Then>
          <PinList
            dataSource={dataSource}
            hasMore={isRemix ? hasMore : false}
            showDownload={showDownload}
            masonryOptions={{
              canLayout: userReady,
            }}
            onLoadMore={isRemix ? onLoadMore : undefined}
            onLoginLimit={onLoginLimit}
            endMessage={
              loadingLimit ? undefined : (
                <If condition={hasMore}>
                  <Then>
                    <If condition={isRemix || loading}>
                      <Then>
                        <div className={styles.loading}>
                          <HBLoading />
                        </div>
                      </Then>
                      <Else>
                        <div className={styles.end}>
                          <Button
                            className={styles.button}
                            type="text"
                            size="large"
                            onClick={onLoadMore}
                          >
                            展开更多
                          </Button>
                        </div>
                      </Else>
                    </If>
                  </Then>
                  <Else>
                    <div
                      data-gd-expose="resource_loc_expose"
                      data-resource-title="没有更多了"
                      data-resource-type="文字"
                      className={styles.noMore}
                    >
                      {isRemix
                        ? `更多关于「${title}」内容即将更新，敬请期待～`
                        : "没有更多了"}
                    </div>
                  </Else>
                </If>
              )
            }
          />
        </Then>
        <Else>
          <If condition={loading}>
            <Then>
              <div className={styles.largeLoading}>
                <HBLoading />
              </div>
            </Then>
            <Else>
              <TopicEmpty title={isRemix ? title : undefined} />
            </Else>
          </If>
        </Else>
      </If>
    </div>
  );
}

export default function TopicPinList({ values, defaultPins, showDownload }) {
  const [dataSource, setDataSource] = useState(defaultPins || []);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(defaultPins?.length <= 0);
  const pageRef = useRef(defaultPins?.length > 0 ? 2 : 1);
  const initRef = useRef(defaultPins?.length > 0);

  const queryAction = useMemo(() => {
    if (initRef.current) {
      if (values) {
        initRef.current = false;
      }
    } else {
      pageRef.current = 1;
      setDataSource([]);
      setHasMore(true);
    }

    return async () => {
      if (!values) {
        return;
      }

      setLoading(true);
      const result = await getTopicPins({
        per_page: 40,
        page: pageRef.current,
        filter_ids: values?.ids || [],
        sort: values?.sort || "all",
        position: "topics",
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
      setLoading(false);
    };
  }, [values]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0 && !initRef.current) {
      pageRef.current++;
      queryAction();

      pvTracker(getPageData());
    }
  }, [dataSource, queryAction]);

  return (
    <div
      className={styles.list}
      data-sort-type={values?.sortName}
      data-filter-one={values?.filterOne}
    >
      <If condition={dataSource.length > 0}>
        <Then>
          <PinList
            dataSource={dataSource}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            showDownload={showDownload}
          />
        </Then>
        <Else>
          <If condition={loading}>
            <Then>
              <div className={styles.loading}>
                <HBLoading />
              </div>
            </Then>
            <Else>
              <TopicEmpty />
            </Else>
          </If>
        </Else>
      </If>
    </div>
  );
}
